import { action, computed, observable } from 'mobx';
import _ from 'lodash';
import { LiveVirtualClass } from 'types/liveClass';
import { TFunction } from 'i18next';
import { extractFromSlug } from 'helpers/utils';
import CatalogStore from 'stores/Catalog';
import type { CatalogEntry, LiveEvent } from 'types/';
import type { EnablementsQuotas } from 'types/user';
import type { LmsClassEnrollment } from 'types/class';
import { LIVE_CLASS_CANCELLATION_WINDOW } from 'config/constants';
import { LandingTab } from 'components/Landing/types';
import { DateTime } from 'luxon';
import { LiveClassEnrollmentErrorStates } from '../types/liveClass';
import {
  getAllEnrolledLiveClass,
  getOfferingUpcomingLiveClass,
  getOfferingEnrolledLiveClass,
  enrollLiveClass,
  rescheduleLiveClass,
} from '../services/LiveClassService';
import {
  getPartnerOfferingUpcomingLiveClass,
  getPartnerOfferingEnrolledLiveClass,
  getPartnerEntitlementsQuota,
  enrollPartnerLiveClass,
  reschedulePartnerLiveClass,
  unenrollPartnerLiveClass,
} from '../services/PartnerLiveClassService';
import { getPartnerLvcEnrollments } from '../services/PartnerEnrollmentService';
import PartnerStudentsStore from '../components/Partner/Enrollments/store';
import type {
  PartnerLvcEnrollment,
  PartnerScheduleBlockReason,
} from '../types/partnerEnrollment';
import type { IRootStore } from './IRootStore';

interface EnrollmentSuccessAlert {
  title?: string;
  message?: string;
}
class LiveClassStore {
  @observable offeringLiveClassesLoaded = false;

  @observable offeringEnrolledLiveClassesLoaded = false;

  @observable enrolledLvc: LiveVirtualClass[] = [];

  @observable isLoadingEnrolledLvc = false;

  @observable upcomingLvc: LiveVirtualClass[] = [];

  @observable offeringLiveClasses: LiveVirtualClass[] = [];

  @observable offeringEnrolledLiveClass = [];

  @observable partnerLearnerEnrolledLiveClasses: LiveVirtualClass[] = [];

  @observable instructorsDetails: any = {};

  @observable loaded = false;

  @observable loading = false;

  @observable offeringSlug = '';

  @observable partnerScheduleUserUuid: string | null = null;

  @observable partnerLearnerQuotas: Partial<EnablementsQuotas> | null = null;

  @observable partnerLearnerSeat: PartnerLvcEnrollment | null = null;

  @observable enrollmentSuccessAlert: EnrollmentSuccessAlert = {};

  @observable startsSoonWarningDays = LIVE_CLASS_CANCELLATION_WINDOW;

  rootStore: IRootStore;

  constructor(rootStore: IRootStore) {
    this.rootStore = rootStore;
  }

  @action setPartnerScheduleContext(userUuid: string | null) {
    this.partnerScheduleUserUuid = userUuid;
    if (!userUuid) {
      this.partnerLearnerQuotas = null;
      this.partnerLearnerEnrolledLiveClasses = [];
      this.partnerLearnerSeat = null;
    }
  }

  @computed
  get partnerScheduleBlockReason(): PartnerScheduleBlockReason | null {
    if (!this.isPartnerScheduling) {
      return null;
    }
    const enrolled = Array.isArray(this.offeringEnrolledLiveClass)
      ? (this.offeringEnrolledLiveClass[0] as LiveVirtualClass)
      : null;
    return PartnerStudentsStore.resolveScheduleBlockReason(
      this.partnerLearnerSeat,
      enrolled,
    );
  }

  @computed get isPartnerScheduleReadOnly(): boolean {
    return this.partnerScheduleBlockReason != null;
  }

  @computed get partnerScheduleBlockDateIso(): string | undefined {
    const reason = this.partnerScheduleBlockReason;
    const seat = this.partnerLearnerSeat;
    if (reason === 'expired') {
      return seat?.end_time;
    }
    if (reason === 'future') {
      return seat?.start_time;
    }
    return undefined;
  }

  @computed get scheduleQuotas(): EnablementsQuotas | null | undefined {
    if (this.isPartnerScheduling) {
      return this.partnerLearnerQuotas as EnablementsQuotas;
    }
    return this.rootStore.userStore.enablementsQuotas;
  }

  @computed get isPartnerScheduling(): boolean {
    return Boolean(this.partnerScheduleUserUuid);
  }

  @action getAllEnrolledLvcForCurrentUser = async () => {
    try {
      this.isLoadingEnrolledLvc = true;
      const enrolledLvc = await getAllEnrolledLiveClass();
      if (Array.isArray(enrolledLvc?.items)) {
        this.enrolledLvc = enrolledLvc.items || [];
      }
    } catch (err) {
      console.warn(`Couldn't fetch enrolledLvc`, err);
    } finally {
      this.isLoadingEnrolledLvc = false;
    }
  };

  @action getAllUpcomingLvcForCurrentUser = async () => {
    try {
      const upcomingLvc = await getOfferingUpcomingLiveClass();
      if (Array.isArray(upcomingLvc?.items)) {
        this.upcomingLvc = upcomingLvc.items || [];
      }
    } catch (err) {
      console.warn(`Couldn't fetch upcomingLvc`, err);
    }
  };

  @computed get daysUntilStart() {
    const { start_time: startTime, timezone } =
      this.offeringEnrolledLiveClass?.[0] || {};
    let daysUntilStart = 0;
    if (startTime && timezone) {
      const startDay = DateTime.fromISO(startTime)
        .setZone(timezone)
        .startOf('day');
      const now = DateTime.now().setZone(timezone).startOf('day');
      daysUntilStart = startDay.diff(now).toMillis() / (1000 * 60 * 60 * 24);
    }

    return daysUntilStart;
  }

  @computed get isStartingSoon() {
    return this.daysUntilStart <= this.startsSoonWarningDays;
  }

  @computed get enrolledLvcByCode(): Map<string, LiveVirtualClass[]> {
    const grouped = new Map<string, LiveVirtualClass[]>();
    this.enrolledLvc.forEach((enrollment) => {
      const code = enrollment.offering_code;
      if (!grouped.has(code)) {
        grouped.set(code, []);
      }
      grouped.get(code)!.push(enrollment);
    });
    return grouped;
  }

  @computed get upcomingLvcByCode(): Map<string, LiveVirtualClass[]> {
    const grouped = new Map<string, LiveVirtualClass[]>();
    this.upcomingLvc.forEach((enrollment) => {
      const code = enrollment.offering_code;
      if (!grouped.has(code)) {
        grouped.set(code, []);
      }
      grouped.get(code)!.push(enrollment);
    });
    return grouped;
  }

  @computed get scheduledOfferingLiveClasses() {
    return this.offeringLiveClasses;
  }

  @computed get offeringLiveClassAggregateList(): LiveVirtualClass[] {
    // Ensure all required data is loaded before proceeding
    if (
      !this.offeringLiveClassesLoaded ||
      !this.offeringEnrolledLiveClassesLoaded
    ) {
      return [];
    }

    return this.offeringLiveClasses ?? [];
  }

  // eslint-disable-next-line consistent-return
  @computed get offeringCode() {
    if (this.offeringSlug) {
      const { code } = extractFromSlug(this.offeringSlug);
      return code;
    }
  }

  @computed get enrolledQuotaAllowed() {
    return Number(this.scheduleQuotas?.live_class_enrolled?.allowed ?? 0);
  }

  @computed get enrolledQuotaUsed() {
    return Number(this.scheduleQuotas?.live_class_enrolled?.used ?? 0);
  }

  @computed get isEnrolledQuotaReached() {
    return this.enrolledQuotaUsed >= this.enrolledQuotaAllowed;
  }

  getPerSkuQuotaAllowedUsed(quotaSku: string, getAllowedNumber = true) {
    const quota = this.scheduleQuotas?.[quotaSku] || {};

    // If there's no quota data or no offering code, we can't proceed,
    // so we return value will be 0.
    if (!quota || !this.offeringCode) return 0;

    if (getAllowedNumber) {
      return Number(
        quota?.allowed?.[this.offeringCode] || quota?.allowed?.default || 0,
      );
    }

    return Number(_.get(quota, `used.${this.offeringCode}`, 0));
  }

  @computed get enrolledPerSkuQuotaAllowed() {
    return this.getPerSkuQuotaAllowedUsed('live_class_enrolled_per_sku');
  }

  @computed get enrolledPerSkuQuotaUsed() {
    return this.getPerSkuQuotaAllowedUsed('live_class_enrolled_per_sku', false);
  }

  @computed get isEnrolledPerSkuQuotaReached() {
    return this.enrolledPerSkuQuotaUsed >= this.enrolledPerSkuQuotaAllowed;
  }

  @computed get completedPerSkuQuotaAllowed() {
    return this.getPerSkuQuotaAllowedUsed('live_class_completed_per_sku');
  }

  @computed get completedPerSkuQuotaUsed() {
    return this.getPerSkuQuotaAllowedUsed(
      'live_class_completed_per_sku',
      false,
    );
  }

  @computed get isCompletedPerSkuQuotaReached() {
    return this.completedPerSkuQuotaUsed >= this.completedPerSkuQuotaAllowed;
  }

  @computed get isEnrolledPlusCompletedPerSkuQuotaReached() {
    const used = this.enrolledPerSkuQuotaUsed + this.completedPerSkuQuotaUsed;

    return used >= this.completedPerSkuQuotaAllowed;
  }

  @computed get canScheduleLiveClass() {
    return (
      this.offeringLiveClassesLoaded &&
      this.offeringEnrolledLiveClassesLoaded &&
      !this.isEnrolledQuotaReached &&
      !this.isEnrolledPerSkuQuotaReached &&
      !this.isCompletedPerSkuQuotaReached &&
      !this.isEnrolledPlusCompletedPerSkuQuotaReached
    );
  }

  @computed get isAnyQuotaReached() {
    return (
      this.isEnrolledQuotaReached === true ||
      this.isCompletedPerSkuQuotaReached === true ||
      this.isEnrolledPerSkuQuotaReached === true ||
      this.isEnrolledPlusCompletedPerSkuQuotaReached === true
    );
  }

  @computed get getCourseTitle() {
    if (!this.offeringCode) {
      return '';
    }

    let catalogOffering: string | Record<string, CatalogEntry> | CatalogEntry;

    catalogOffering =
      this.rootStore.catalogStore.groupedCatalogEntries[this.offeringCode] ||
      this.rootStore.catalogStore.groupedAllCatalogEntries[this.offeringCode] ||
      {};

    catalogOffering =
      catalogOffering[this.offeringCode] ||
      (Object.values(catalogOffering)?.length > 0 &&
        Object.values(catalogOffering)[0]) ||
      {};

    return (
      CatalogStore.getTitleWithFallback(
        catalogOffering as CatalogEntry,
        this.rootStore.uiStore.currentLanguage,
      ) || ''
    );
  }

  @computed get overlapEnrolledOfferingsMap() {
    const scheduledLiveClass = this.scheduledOfferingLiveClasses;
    let enrolledLiveEvents: LiveEvent[] = [];

    if (this.isPartnerScheduling) {
      if (
        !this.offeringEnrolledLiveClassesLoaded ||
        scheduledLiveClass.length === 0
      ) {
        return '';
      }

      enrolledLiveEvents = this.partnerLearnerEnrolledLiveClasses.map(
        (enrollment) => ({
          id: enrollment.class_uuid || enrollment.offering_id,
          startTime: enrollment.start_time,
          endTime: enrollment.end_time,
          title: enrollment.offering_name || enrollment.offering_code,
          offeringId: enrollment.offering_id,
          lmsOfferingSlug: enrollment.offering_slug,
          type: 'lvc' as const,
          isCurrent: false,
          slug: enrollment.offering_slug,
        }),
      );
    } else {
      const {
        enrolledLiveEvents: userEnrolledLiveEvents,
        isLoadingLiveEvents,
      } = this.rootStore.classesStore;

      if (
        !userEnrolledLiveEvents?.length ||
        isLoadingLiveEvents ||
        scheduledLiveClass.length === 0
      ) {
        return '';
      }

      enrolledLiveEvents = userEnrolledLiveEvents;
    }

    if (!enrolledLiveEvents.length) {
      return '';
    }

    const overlapOfferings = {};

    // Overlap occurs if: enrolledStartTime < lvcEndTime AND enrolledEndTime > lvcStartTime
    const checkOverlap = (
      enrolledStartTime,
      enrolledEndTime,
      lvcStartTime,
      lvcEndTime,
    ) =>
      DateTime.fromISO(enrolledStartTime)
        .diff(DateTime.fromISO(lvcEndTime))
        .toMillis() < 0 &&
      DateTime.fromISO(enrolledEndTime)
        .diff(DateTime.fromISO(lvcStartTime))
        .toMillis() > 0;

    const addRouteInfo = (OverLapOffering: LiveEvent) => {
      const { id, slug, type, isCurrent, lmsOfferingSlug } = OverLapOffering;

      let routeName = '';
      let routeParams = {};

      if (type === 'premvt' && id && slug) {
        routeName = 'sessions:view';
        routeParams = {
          sessionid: id,
          sessionslug: slug,
        };
      } else if (type === 'exam' && slug) {
        routeName = 'exams:view';
        routeParams = {
          slug,
        };
      } else if ((type === 'vt' || type === 'ilt') && id) {
        routeName = 'classes:view';
        routeParams = { uuid: id };
      } else if (type === 'lvc' && id) {
        if (!isCurrent && lmsOfferingSlug) {
          routeName = 'courses:overview';
          routeParams = {
            course: lmsOfferingSlug,
            tab: LandingTab.LiveClass,
          };
        } else {
          routeName = 'classes:view';
          routeParams = { uuid: id };
        }
      }

      return {
        ...OverLapOffering,
        routeName,
        routeParams,
      };
    };

    enrolledLiveEvents.forEach((enrolledEvent) => {
      const enrolledStartTime = enrolledEvent.startTime;
      const enrolledEndTime = enrolledEvent.endTime;

      if (!enrolledStartTime || !enrolledEndTime) {
        return;
      }
      scheduledLiveClass.forEach((LvcEvent) => {
        const lvcStartTime = LvcEvent.start_time;
        const lvcEndTime = LvcEvent.end_time;
        const offeringId = LvcEvent.offering_id;

        if (
          !lvcStartTime ||
          !lvcEndTime ||
          enrolledEvent.offeringId === offeringId
        )
          return;

        if (
          checkOverlap(
            enrolledStartTime,
            enrolledEndTime,
            lvcStartTime,
            lvcEndTime,
          )
        ) {
          // conflicting enrolled event (enrolled) against the schedule offering (lvc)
          overlapOfferings[offeringId] = addRouteInfo(enrolledEvent);
        }
      });
    });

    return overlapOfferings;
  }

  static getUseLiveClassEnrollErrorMessage = (
    errorDetail: any,
    t: TFunction,
  ) => {
    let errorMessage = '';
    const errorType = Object.values(LiveClassEnrollmentErrorStates).find((e) =>
      errorDetail?.includes(e),
    );

    switch (errorType) {
      case LiveClassEnrollmentErrorStates.UnenrollEnrollmentNotExist:
        errorMessage = t(
          'This live class is no longer available. No cancellation is needed.',
        );
        break;
      case LiveClassEnrollmentErrorStates.InvalidStatus:
      case LiveClassEnrollmentErrorStates.ClassNotExist:
      case LiveClassEnrollmentErrorStates.EnrollmentNotExist:
      case LiveClassEnrollmentErrorStates.ClassStarted:
      case LiveClassEnrollmentErrorStates.NotLiveClass:
      case LiveClassEnrollmentErrorStates.NotCustomer:
      case LiveClassEnrollmentErrorStates.NotInternal:
        errorMessage = t(
          'This live class is no longer available. Please choose a different one.',
        );
        break;
      case LiveClassEnrollmentErrorStates.CannotRescheduleSameOffering:
        errorMessage = t(
          'The same live class cannot be selected again. Please choose a different one.',
        );
        break;
      case LiveClassEnrollmentErrorStates.CannotCancel:
        errorMessage = t(
          'Live classes can only be rescheduled more than 14 days before the start date.',
        );
        break;
      case LiveClassEnrollmentErrorStates.ClassFull:
        errorMessage = t(
          'This live class is fully booked. Please choose a different time slot.',
        );
        break;
      case LiveClassEnrollmentErrorStates.ConflictingEnrollments:
        errorMessage = t(
          'This live class conflicts with another one you have already scheduled. Please choose a different time slot.',
        );
        break;
      case LiveClassEnrollmentErrorStates.QuotaReachedEnrolledSku:
        errorMessage = t(
          'The scheduling limit for this course has been reached. This live class cannot be scheduled.',
        );
        break;
      case LiveClassEnrollmentErrorStates.QuotaReachedCompletedSku:
        errorMessage = t(
          'The attendance limit for this course has been reached. This live class cannot be scheduled.',
        );
        break;
      case LiveClassEnrollmentErrorStates.QuotaReachedEnrolled:
        errorMessage = t(
          `You have reached the maximum number of live classes that can be scheduled at one time. To schedule this class, please cancel an existing one or attend a scheduled class first.`,
        );
        break;
      case LiveClassEnrollmentErrorStates.QuotaReachedEnrolledPlusCompletedSku:
        errorMessage = t(
          `You have reached the maximum number of classes allowed to attend for this course. To schedule this class, please cancel one of your scheduled classes.`,
        );
        break;
      case LiveClassEnrollmentErrorStates.NotWithInEnrollment:
        errorMessage = t(
          'The class ends after your subscription expires. Renew your subscription or choose an earlier time.',
        );
        break;
      case LiveClassEnrollmentErrorStates.EnrollmentPeriodEnded:
        errorMessage = t(
          'This live class enrollment period has ended. Please choose a different one.',
        );
        break;
      default:
        errorMessage = t(
          'Something went wrong while scheduling the live class. Please try again. If the issue continues, contact support.',
        );
    }
    return errorMessage;
  };

  static getUseUnEnrollErrorMessage = (errorDetail: any, t: TFunction) => {
    let errorMessage = '';
    const errorType = Object.values(LiveClassEnrollmentErrorStates).find((e) =>
      errorDetail?.includes(e),
    );

    switch (errorType) {
      case LiveClassEnrollmentErrorStates.CannotCancelEnrollmentLms:
        errorMessage = t(
          'This live class cannot be canceled because it was not enrolled by you.',
        );
        break;
      case LiveClassEnrollmentErrorStates.CannotCancel:
        errorMessage = t(
          'Live classes can only be canceled more than 14 days before the start date.',
        );
        break;

      default:
        errorMessage = t(
          'Something went wrong while canceling the live class. Please try again. If the issue continues, contact support.',
        );
    }
    return errorMessage;
  };

  @action loadPartnerLearnerSeat = async (offeringSlug: string) => {
    const { code } = extractFromSlug(offeringSlug);
    if (!this.partnerScheduleUserUuid || !code) {
      this.partnerLearnerSeat = null;
      return;
    }
    try {
      const enrollments = await getPartnerLvcEnrollments();
      this.partnerLearnerSeat = PartnerStudentsStore.pickPartnerLearnerSeat(
        enrollments,
        this.partnerScheduleUserUuid,
        code,
      );
    } catch (e) {
      console.error('Error loading partner learner seat', e);
      this.partnerLearnerSeat = null;
    }
  };

  @action loadPartnerLearnerQuotas = async () => {
    if (!this.partnerScheduleUserUuid) {
      this.partnerLearnerQuotas = null;
      return;
    }

    try {
      const quotas = await getPartnerEntitlementsQuota(
        this.partnerScheduleUserUuid,
      );
      this.partnerLearnerQuotas = quotas ?? null;
    } catch (e) {
      console.error('Error loading learner quotas for partner schedule', e);
      this.partnerLearnerQuotas = null;
    }
  };

  @action getOfferingUpcomingLiveClass = async (
    offeringSlug: string,
    force = true,
  ) => {
    if (!force) {
      return this.offeringLiveClasses;
    }
    try {
      this.offeringLiveClassesLoaded = false;
      if (this.isPartnerScheduling) {
        await this.loadPartnerLearnerQuotas();
        await this.loadPartnerLearnerSeat(offeringSlug);
      }
      const liveClassOfferings = this.isPartnerScheduling
        ? await getPartnerOfferingUpcomingLiveClass(
            offeringSlug,
            this.partnerScheduleUserUuid!,
          )
        : await getOfferingUpcomingLiveClass(offeringSlug);
      if (liveClassOfferings) {
        this.offeringLiveClasses = liveClassOfferings?.items;
      }
    } catch (e) {
      console.error('Error while getting premium sessions', e);
    } finally {
      this.offeringLiveClassesLoaded = true;
    }

    return this.offeringLiveClasses;
  };

  @action getOfferingEnrolledLiveClass = async (offeringSlug: string) => {
    try {
      this.offeringEnrolledLiveClassesLoaded = false;
      const enrolledOfferings = this.isPartnerScheduling
        ? await getPartnerOfferingEnrolledLiveClass(
            this.partnerScheduleUserUuid!,
          )
        : await getOfferingEnrolledLiveClass(offeringSlug);
      if (enrolledOfferings) {
        const items = enrolledOfferings.items ?? [];
        if (this.isPartnerScheduling) {
          this.partnerLearnerEnrolledLiveClasses = items;
          if (offeringSlug) {
            const { code } = extractFromSlug(offeringSlug);
            this.offeringEnrolledLiveClass = code
              ? items.filter((item) => item.offering_code === code)
              : items;
          } else {
            this.offeringEnrolledLiveClass = items;
          }
        } else {
          this.offeringEnrolledLiveClass = items;
        }
      }
    } catch (e) {
      console.error('Error while getting premium session by id', e);
      return {};
    } finally {
      if (this.isPartnerScheduling && offeringSlug) {
        await this.loadPartnerLearnerSeat(offeringSlug);
      }
      this.offeringEnrolledLiveClassesLoaded = true;
    }

    return this.offeringEnrolledLiveClass;
  };

  @action enrollUser = async (
    offeringId: string,
    rescheduleFromOfferingId: string,
    refreshEnrollments = true,
  ): Promise<LmsClassEnrollment> => {
    let response: LmsClassEnrollment;

    try {
      if (this.isPartnerScheduling) {
        if (offeringId && rescheduleFromOfferingId) {
          response = await reschedulePartnerLiveClass(
            offeringId,
            rescheduleFromOfferingId,
            this.partnerScheduleUserUuid!,
            this.offeringSlug,
          );
        } else {
          response = await enrollPartnerLiveClass(
            offeringId,
            this.partnerScheduleUserUuid!,
            this.offeringSlug,
          );
        }
      } else if (offeringId && rescheduleFromOfferingId) {
        response = await rescheduleLiveClass(
          offeringId,
          rescheduleFromOfferingId,
        );
      } else {
        response = await enrollLiveClass(offeringId);
      }

      if (refreshEnrollments) {
        await this.getOfferingEnrolledLiveClass(this.offeringSlug);
        if (this.isPartnerScheduling) {
          await this.loadPartnerLearnerQuotas();
        } else {
          await this.rootStore.userStore.getEnablements();
          await this.rootStore.classesStore.getClassesForCurrentUser();
        }
      }

      return response;
    } catch (e) {
      throw e;
    }
  };

  @action unenrollPartnerUser = async (offeringId: string) => {
    if (!this.isPartnerScheduling) {
      throw new Error('Partner scheduling context is required');
    }
    await unenrollPartnerLiveClass(
      offeringId,
      this.partnerScheduleUserUuid!,
      this.offeringSlug,
    );

    await this.getOfferingEnrolledLiveClass(this.offeringSlug);
    await this.loadPartnerLearnerQuotas();
  };
}

export default LiveClassStore;
